import * as React from "react";
import { useEffect, useState } from "react";
import { AlertColor, Box, CircularProgress, FormControl, FormHelperText, InputAdornment, Paper } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import { DateTime } from "luxon";
import { IPackage, usePackagesService } from "../services/registry/PackageService";
import { Notify } from "./Notify";

export interface PackagePickerProps {
  onPackageSelected?: (pkg: IPackage) => void;
  error?: string;
  searchLabel?: string;
}
export const PackagePicker = ({ onPackageSelected, error, searchLabel }: PackagePickerProps) => {
  const packagesService = usePackagesService();

  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [packages, setPackages] = useState<IPackage[]>([]);
  const [filteredPackages, setFilteredPackages] = useState<IPackage[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  const [selectedItem, setSelectedItem] = useState<IPackage | undefined>(undefined);
  const [internalError, setInternalError] = useState<string | undefined>(undefined);

  useEffect(() => {
    fetchPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFilteredPackages(
      packages.filter(
        (pkg) => pkg.id.includes(searchValue) || (pkg.wads && pkg.wads.some((wad) => wad.id?.includes(searchValue))),
      ),
    );
  }, [searchValue, packages]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await packagesService.listPackages();
      const list = res.list as IPackage[];
      const sortedPackages = list.sort((a, b) => b.createdTime - a.createdTime);
      setPackages(sortedPackages);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to load packages.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const handleSelection = (pkg: IPackage) => {
    setSelectedItem(pkg);
    setInternalError(undefined);
    onPackageSelected?.(pkg);
  };

  // Use external error if provided, otherwise use internal error state
  const displayError = error || internalError;

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      {loading ? (
        <CircularProgress />
      ) : (
        <FormControl sx={{ width: "100%" }} error={!!displayError}>
          <TextField
            id="search-input"
            label={searchLabel ?? "Search"}
            variant="standard"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            style={{ marginBottom: 12, width: "100%" }}
            error={!!displayError}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Paper elevation={0} style={{ maxHeight: 300, overflow: "auto" }}>
            <List dense disablePadding>
              {filteredPackages.map((pkg) => {
                const creationDate = DateTime.fromSeconds(pkg.createdTime).toLocaleString(
                  DateTime.DATETIME_SHORT_WITH_SECONDS,
                );
                // Display the package ID and its state, and first few WADs if available
                let wadsSummary = "No WADs";
                if (pkg.wads && pkg.wads.length > 0) {
                  wadsSummary = `WADs: ${pkg.wads.slice(0, 2).join(", ")}${pkg.wads.length > 2 ? "..." : ""}`;
                }
                return (
                  <ListItem key={pkg.id} disableGutters>
                    <ListItemButton selected={selectedItem?.id === pkg.id} onClick={() => handleSelection(pkg)}>
                      <ListItemText primary={`${pkg.id} (${pkg.state})`} secondary={`${creationDate} - ${wadsSummary}`} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Paper>

          {displayError && <FormHelperText error>{displayError}</FormHelperText>}
        </FormControl>
      )}
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
    </Box>
  );
};
